import { useMemo, useState } from 'react';
import { Select } from '@nazka/nazka.mapframe.components';

import InfoIcon from '@App/components/InfoIcon/InfoIcon';
import theme from '@style/theme.json';
import { useParameters } from '@services/query.service';
import { useTranslation } from '@utils/i18n/i18n';

import {
  CONTRIBUTING_PARAMETER_OPTIONS,
  CONTRIBUTING_PARAMETERS_BY_INDICATOR,
  PARAMETER_DEFINITIONS,
} from './contributing-parameters.constants';
import { formatValue } from './contributing-parameters.utils';

import './contributing-parameters.css';

function ContributingParameters({ month, year }) {
  const [selectedIndicator, setSelectedIndicator] = useState(CONTRIBUTING_PARAMETER_OPTIONS[0]);

  const { t } = useTranslation();
  const { data: parameters, isLoading, isError } = useParameters();

  const selectedParameterSet = useMemo(() => {
    if (!parameters?.length) {
      return null;
    }

    return parameters.find(parameter => parameter.year === year && parameter.month === month) ?? null;
  }, [month, parameters, year]);

  const parameterCards = useMemo(() => {
    const keys = CONTRIBUTING_PARAMETERS_BY_INDICATOR[selectedIndicator.id] ?? [];

    return keys
      .map((key) => {
        const definition = PARAMETER_DEFINITIONS[key];

        return {
          key,
          label: definition.label,
          unit: definition.unit,
          description: definition.description,
          value: selectedParameterSet?.[key],
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedIndicator.id, selectedParameterSet]);

  return (
    <div className="contributing-parameters">
      <div className="contributing-parameters__header">
        <h3>{t('Contributing parameters')}</h3>
        <InfoIcon
          tooltip={t('Contributing parameters tooltip')}
          type="filled"
          size="22px"
          color={theme.color.primary.main}
        />
      </div>
      <Select
        className="contributing-parameters__select"
        options={CONTRIBUTING_PARAMETER_OPTIONS}
        value={selectedIndicator}
        onInputChange={setSelectedIndicator}
      />
      {isLoading && <p className="contributing-parameters__status">{t('Loading parameters...')}</p>}
      {isError && <p className="contributing-parameters__status">{t('Failed to load parameters.')}</p>}
      {!isLoading && !isError && !selectedParameterSet && (
        <p className="contributing-parameters__status">{t('No parameter data available for the selected month and year.')}</p>
      )}
      {!isLoading && !isError && selectedParameterSet && (
        <div className="contributing-parameters__grid">
          {parameterCards.map(({ key, label, unit, description, value }) => {
            const labelText = `${label}: ${formatValue(value)}${unit ? ` ${unit}` : ''}`;

            return (
              <div key={key} className="contributing-parameters__card">
                <div className="contributing-parameters__card__content">
                  <span className="contributing-parameters__card__text" title={labelText}>
                    {labelText}
                  </span>
                  <InfoIcon
                    tooltip={description}
                    type="filled"
                    size="18px"
                    color={theme.color.primary.main}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ContributingParameters;
